import { AiGeneratedLabel, Text } from '@/ui/design-system/components';
import { Check, LoaderCircle, Pencil } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

// Title + analysis block at the top of a report. Analysis is read-only (the
// report view drives (re)generation via the synthesize endpoint and feeds the
// latest text, a loading flag, and an error + onRetry down). The title is
// inline-editable when `editable`: the pencil opens an input, and committing
// (blur, Enter, or the check) bubbles the new value up via `onTitleSave` — the
// report view persists it. Edit affordances are hidden in preview mode.
function ReportSummary({
    title,
    analysis,
    isLoading,
    error,
    onRetry,
    editable = false,
    onTitleSave,
}) {
    const [editingTitle, setEditingTitle] = useState(false);
    const [draft, setDraft] = useState('');
    // One commit per edit session: blur after Enter/check would otherwise persist
    // twice. Reset when a fresh edit starts.
    const committedRef = useRef(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (editingTitle) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [editingTitle]);

    const startEdit = () => {
        setDraft(title || '');
        committedRef.current = false;
        setEditingTitle(true);
    };

    const commit = () => {
        if (committedRef.current) return;
        committedRef.current = true;
        setEditingTitle(false);
        const next = draft.trim();
        if (next && next !== title) onTitleSave?.(next);
    };

    const cancel = () => {
        committedRef.current = true; // block a trailing blur-commit
        setEditingTitle(false);
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
                {editingTitle ? (
                    // Keeps the heading's size/casing but sizes to its content (via
                    // the `size` attr) instead of stretching full-width, and wears
                    // the same focus ring as the design-system Input.
                    <input
                        ref={inputRef}
                        value={draft}
                        size={Math.max(draft.length, 8)}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commit}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                commit();
                            } else if (e.key === 'Escape') {
                                cancel();
                            }
                        }}
                        aria-label="Report title"
                        className="max-w-full rounded-md border border-input bg-[var(--background)] px-2 py-0.5 text-2xl font-semibold leading-9 text-foreground transition-colors focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
                    />
                ) : (
                    <Text
                        as="h1"
                        size="2xl"
                        className="font-semibold normal-case leading-9 break-words"
                    >
                        {title || 'Untitled report'}
                    </Text>
                )}
                {editable && (
                    <button
                        type="button"
                        onClick={editingTitle ? commit : startEdit}
                        onMouseDown={
                            editingTitle ? (e) => e.preventDefault() : undefined
                        }
                        aria-label={editingTitle ? 'Save title' : 'Edit title'}
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
                    >
                        {editingTitle ? (
                            <Check size={16} />
                        ) : (
                            <Pencil size={16} />
                        )}
                    </button>
                )}
            </div>
            {(analysis || isLoading) && <AiGeneratedLabel className="mt-1" />}
            {analysis && (
                <Text
                    size="sm"
                    className="max-w-2xl whitespace-pre-wrap break-words text-muted-foreground"
                >
                    {analysis}
                </Text>
            )}
            {isLoading && (
                <div className="flex items-center gap-2 text-muted-foreground">
                    <LoaderCircle size={14} className="animate-spin" />
                    <Text size="sm" color="muted">
                        Analyzing the latest data…
                    </Text>
                </div>
            )}
            {error && !isLoading && (
                <div className="flex items-center gap-2">
                    <Text size="sm" color="muted">
                        Problem streaming analysis.
                    </Text>
                    <button
                        type="button"
                        onClick={onRetry}
                        className="text-sm font-medium text-green-600 hover:underline"
                    >
                        Retry
                    </button>
                </div>
            )}
            {!analysis && !isLoading && !error && (
                <Text size="sm" color="muted">
                    No analysis yet.
                </Text>
            )}
        </div>
    );
}

export default ReportSummary;
